/**
 * Murphy Door Configurator - Error Handler Support Module
 *
 * PURPOSE: Centralized error management, display, and recovery coordination.
 *
 * PHASE: 2 (Event-Driven CCP - Strict Enforcement)
 * MODULE TYPE: Support (support-)
 * GOLDEN STANDARD REFERENCE: md-config-bl-material-selector.js (200/200)
 *
 * CCP COMPLIANCE:
 * - CONSTANTS.EVENTS.* exclusively (no local EVENT_TYPES - Pattern 19)
 * - publishEvent() async helper with try/catch (Pattern 2, 11)
 * - 5-stage event lifecycle: STARTED -> VALIDATION -> PROGRESS -> COMPLETED/FAILED (Pattern 3)
 * - Event consumer handlers for bootstrap + error events (Pattern 4)
 * - Event replay ready via bootstrapComplete guard (Pattern 5)
 * - Bootstrap runtime protection via SYSTEM_STATE_FROM_PPD_READY (Pattern 6)
 * - No raw Shopify data access (Patterns 7, 8)
 * - Data request events via conductor delegation (Pattern 9)
 * - UI update events via CoreEventBus (Pattern 10)
 * - Performance tracking with thresholds (Patterns 12, 13)
 * - Try/catch + FAILED events on all operations (Pattern 14)
 * - Cleanup arrays for all subscriptions (Pattern 15)
 * - Initialization guard (Pattern 16)
 * - XSS prevention via DOM.setTextContent (Pattern 17)
 * - CoreEventBus central freeze (Pattern 18 DRY)
 * - Conductor single publication (Pattern 20)
 *
 * ANTI-RECURSION: This module publishes UI_APPEND_ERROR_ELEMENT but does NOT
 * subscribe to error events that could trigger displayError again. All internal
 * errors are logged to console only, never re-published to avoid infinite loops.
 *
 * Dependencies:
 * - md-config-core-constants.js (CONSTANTS.EVENTS.*)
 * - md-config-core-event-bus.js (CoreEventBus)
 * - md-config-ui-updater.js (DOM helper via UI events)
 *
 * This module is used by:
 * - md-config-core-conductor.js (via delegation)
 * - All modules (via CoreEventBus error events)
 */

/** Module uses scoped pattern for Shopify compatibility */
(function (window) {
  'use strict';

  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // PATTERN 1 & 19: GLOBAL CONSTANTS USAGE (10 points each)
  // ============================================================================
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;
  const EVENTS = CONSTANTS.EVENTS;
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;
  const DOM = window.MurphyDoorConfigurator.uiUpdater?.DOM;

  // ============================================================================
  // PATTERN 13: PERFORMANCE THRESHOLDS (10 points)
  // ============================================================================
  const PERFORMANCE_THRESHOLDS = Object.freeze({
    ERROR_DISPLAY: 50, // ms - error display should be fast
    ERROR_RECOVERY: 200, // ms - recovery can take a bit longer
  });

  // Frozen error type constants for external consumers
  const ERROR_TYPES = Object.freeze({
    VALIDATION: 'validation',
    NETWORK: 'network',
    SYSTEM: 'system',
  });

  // ============================================================================
  // PATTERN 16: INITIALIZATION GUARD (10 points)
  // ============================================================================
  let isInitialized = false;

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION (10 points)
  // ============================================================================
  let bootstrapComplete = false;

  // ============================================================================
  // PATTERN 15: CLEANUP ARRAYS (10 points)
  // ============================================================================
  const cleanupHandlers = [];

  // ============================================================================
  // PATTERN 2: publishEvent() ASYNC HELPER (10 points)
  // PATTERN 11: ASYNC EVENT PUBLISHING (10 points)
  // NOTE: Object.freeze() NOT needed - CoreEventBus.publish() freezes centrally (Pattern 18 DRY)
  // ANTI-RECURSION: This helper catches errors internally (console only) to avoid
  // error-handler triggering error-handler loops.
  // ============================================================================
  async function publishEvent(eventType, eventData) {
    try {
      if (CoreEventBus?.publish) {
        await CoreEventBus.publish(eventType, eventData);
      } else {
        isDev() &&
          console.warn(
            `[ErrorHandler] Event bus unavailable, failed to publish: ${eventType}`
          );
      }
    } catch (error) {
      // ANTI-RECURSION: Console only, never re-publish errors from the error handler
      isDev() &&
        console.error(`[ErrorHandler] Error publishing ${eventType}:`, error);
    }
  }

  // ============================================================================
  // PATTERN 12: PERFORMANCE TRACKING (10 points)
  // ============================================================================
  function trackPerformance(operationType, duration) {
    const threshold = PERFORMANCE_THRESHOLDS[operationType];
    if (threshold && duration > threshold) {
      // ANTI-RECURSION: Use console for self-monitoring, not events
      isDev() &&
        console.warn(
          `[ErrorHandler] Slow ${operationType}: ${duration.toFixed(1)}ms (threshold: ${threshold}ms)`
        );
    }
  }

  // ============================================================================
  // CORE ERROR DISPLAY OPERATION
  // PATTERN 3: 5-STAGE EVENT LIFECYCLE (10 points)
  // PATTERN 14: TRY/CATCH + FAILED EVENTS (10 points)
  // ============================================================================

  /**
   * Display an error message to the user via CCP-compliant UI events.
   *
   * @param {string} type - Error type ('validation' | 'network' | 'system')
   * @param {string} message - User-facing error message
   * @param {Object} [details={}] - Additional error context
   * @returns {Object} Result object { success, duration }
   */
  async function displayError(type, message, details = {}) {
    const startTime = performance.now();

    try {
      // STAGE 1: START
      await publishEvent(EVENTS.ERROR_HANDLER_DISPLAY_STARTED, {
        type,
        message,
        details,
        timestamp: Date.now(),
      });

      // STAGE 2: VALIDATION
      if (!message || typeof message !== 'string') {
        await publishEvent(EVENTS.ERROR_HANDLER_DISPLAY_VALIDATION, {
          valid: false,
          errors: ['Invalid or missing error message'],
        });

        await publishEvent(EVENTS.ERROR_HANDLER_DISPLAY_FAILED, {
          error: 'Invalid or missing error message',
          type,
          duration: performance.now() - startTime,
          timestamp: Date.now(),
        });

        return { success: false, error: 'Invalid message' };
      }

      await publishEvent(EVENTS.ERROR_HANDLER_DISPLAY_VALIDATION, {
        valid: true,
        type,
      });

      // STAGE 3: PROGRESS - Create error element and dispatch UI event
      // PATTERN 9: Use DOM utility helper instead of direct main module access
      const wrapper = DOM?.getConfiguratorWrapper?.();

      if (!wrapper) {
        isDev() &&
          console.warn(
            '[ErrorHandler] No configurator wrapper found, skipping UI display'
          );
      }

      // PATTERN 17: XSS PREVENTION (10 points)
      // DOM.createErrorElement uses DOM.setTextContent internally (safe text setting)
      const errorElement = DOM?.createErrorElement?.(message, type);

      if (errorElement && wrapper) {
        // PATTERN 10: UI UPDATE EVENTS (10 points)
        await publishEvent(EVENTS.UI_APPEND_ERROR_ELEMENT, {
          element: errorElement,
          wrapper,
          animate: true,
        });

        // Schedule removal via DOM helper
        setTimeout(() => {
          try {
            DOM?.removeErrorMessage?.(errorElement);
          } catch (removeError) {
            isDev() &&
              console.warn('[ErrorHandler] Error during removal:', removeError);
          }
        }, 5000);
      }

      // STAGE 4: SUCCESS
      const duration = performance.now() - startTime;

      await publishEvent(EVENTS.ERROR_HANDLER_DISPLAY_COMPLETED, {
        type,
        message,
        duration,
        hadWrapper: !!wrapper,
        timestamp: Date.now(),
      });

      trackPerformance('ERROR_DISPLAY', duration);

      return { success: true, duration };
    } catch (error) {
      // STAGE 5: FAILED
      const duration = performance.now() - startTime;

      await publishEvent(EVENTS.ERROR_HANDLER_DISPLAY_FAILED, {
        error: error.message,
        type,
        message,
        duration,
        timestamp: Date.now(),
      });

      // ANTI-RECURSION: Console only for internal failures
      isDev() && console.error('[ErrorHandler] displayError failed:', error);

      return { success: false, error: error.message };
    }
  }

  /**
   * Handle an error with type, message, and optional details.
   * Clears existing errors first, then displays new one.
   *
   * Supports two call signatures for backward compatibility:
   * 1. handleError(type, { message, details }) - original signature
   * 2. handleError(message, error, context) - conductor signature
   *
   * @param {string} typeOrMessage - Error type or message string
   * @param {Object|Error} payloadOrError - { message, details } object or Error instance
   * @param {Object} [context] - Optional context (conductor signature)
   */
  async function handleError(typeOrMessage, payloadOrError, context) {
    if (!bootstrapComplete) {
      isDev() &&
        console.warn(
          '[ErrorHandler] handleError called before bootstrap, queuing'
        );
      return;
    }

    try {
      // Clear existing errors via UI event
      clearErrors();

      // Detect call signature
      if (payloadOrError instanceof Error || context) {
        // Conductor signature: handleError(message, error, context)
        const errorMessage = typeOrMessage;
        const errorObj =
          payloadOrError instanceof Error
            ? payloadOrError
            : new Error(String(payloadOrError));
        const errorContext = context || {};

        isDev() &&
          console.error(
            `[ErrorHandler] ${errorMessage}:`,
            errorObj.message,
            errorContext
          );

        await displayError(
          errorContext.severity || ERROR_TYPES.SYSTEM,
          errorMessage,
          { error: errorObj.message, ...errorContext }
        );
      } else {
        // Original signature: handleError(type, { message, details })
        const type = typeOrMessage;
        const payload = payloadOrError || {};
        await displayError(type, payload.message, payload.details);
      }
    } catch (error) {
      isDev() && console.error('[ErrorHandler] handleError failed:', error);
    }
  }

  /**
   * Handle a critical error - shows persistent error UI.
   *
   * @param {Object} errorInfo - Error info with message property
   * @param {Object} context - Error context
   */
  function handleCriticalError(errorInfo, context) {
    if (!bootstrapComplete) return;

    try {
      const message = errorInfo?.message || 'A critical error occurred';

      // PATTERN 10: UI UPDATE EVENTS - event-first for critical error display
      publishEvent(EVENTS.UI_SHOW_ERROR_NOTIFICATION, {
        message,
        isCritical: true,
        context,
        timestamp: Date.now(),
      });

      isDev() &&
        console.error('[ErrorHandler] Critical error:', errorInfo, context);
    } catch (error) {
      isDev() &&
        console.error('[ErrorHandler] handleCriticalError failed:', error);
    }
  }

  /**
   * Handle a recoverable error - attempts recovery then shows non-blocking message.
   *
   * @param {Object} errorInfo - Error info with message and recoverable properties
   * @param {Object} context - Error context
   */
  async function handleRecoverableError(errorInfo, context) {
    if (!bootstrapComplete) return;

    try {
      if (errorInfo?.recoverable) {
        await attemptRecovery(errorInfo, context);
      }

      // PATTERN 10: UI UPDATE EVENTS - event-first for recoverable error display
      await publishEvent(EVENTS.UI_SHOW_ERROR_NOTIFICATION, {
        message: errorInfo?.message || 'An error occurred',
        isCritical: false,
        context,
        timestamp: Date.now(),
      });
    } catch (error) {
      isDev() &&
        console.error('[ErrorHandler] handleRecoverableError failed:', error);
    }
  }

  /**
   * Attempt to recover from an error by restoring state.
   * PATTERN 3: 5-STAGE LIFECYCLE for recovery operations
   *
   * @param {Object} errorInfo - Error information
   * @param {Object} context - Recovery context
   */
  async function attemptRecovery(errorInfo, context) {
    const startTime = performance.now();

    try {
      await publishEvent(EVENTS.ERROR_HANDLER_RECOVERY_STARTED, {
        errorInfo,
        context,
        timestamp: Date.now(),
      });

      // PATTERN 9: Request state recovery via event (not direct stateManager access)
      await publishEvent(EVENTS.SYSTEM_STATE_RECOVERY_REQUESTED, {
        source: 'error_handler',
        errorInfo,
        context,
        timestamp: Date.now(),
      });

      const duration = performance.now() - startTime;

      // PATTERN 10: UI UPDATE EVENTS - notify recovery message via event
      await publishEvent(EVENTS.UI_SHOW_ERROR_NOTIFICATION, {
        message: 'Configuration restored from backup',
        isCritical: false,
        isRecovery: true,
        timestamp: Date.now(),
      });

      await publishEvent(EVENTS.ERROR_HANDLER_RECOVERY_COMPLETED, {
        duration,
        timestamp: Date.now(),
      });

      trackPerformance('ERROR_RECOVERY', duration);
    } catch (recoveryError) {
      const duration = performance.now() - startTime;

      await publishEvent(EVENTS.ERROR_HANDLER_RECOVERY_FAILED, {
        error: recoveryError.message,
        duration,
        timestamp: Date.now(),
      });

      isDev() &&
        console.error('[ErrorHandler] Recovery failed:', recoveryError);
    }
  }

  /**
   * Clear all error messages via DOM helper.
   */
  function clearErrors() {
    try {
      DOM?.clearAllErrorMessages?.();
    } catch (error) {
      isDev() && console.warn('[ErrorHandler] clearErrors failed:', error);
    }
  }

  /**
   * Display a user-facing error and notify state.
   *
   * @param {string} message - Error message to display
   */
  async function displayUserError(message) {
    if (!bootstrapComplete) return;

    try {
      await displayError(ERROR_TYPES.SYSTEM, message);

      // Notify conductor of error state via event (Pattern 10)
      await publishEvent(EVENTS.SYSTEM_ERROR, {
        message,
        timestamp: Date.now(),
      });
    } catch (error) {
      isDev() &&
        console.error('[ErrorHandler] displayUserError failed:', error);
    }
  }

  /**
   * Handle image layer errors with type-specific messaging.
   *
   * @param {string} type - Error type ('initialization' | 'update' | 'missing')
   * @param {Object} details - Error details with categoryId
   */
  function handleImageLayerError(type, details) {
    if (!bootstrapComplete) return;

    const messages = {
      initialization: 'Failed to initialize configurator image layers',
      update: `Failed to update image layer for category: ${details?.categoryId || 'unknown'}`,
      missing: `Required image layer assets not found for category: ${details?.categoryId || 'unknown'}`,
    };

    const message =
      messages[type] || 'An error occurred with the image layer system';

    logError(message, {
      type: 'ImageLayerError',
      details,
      timestamp: Date.now(),
    });
  }

  /**
   * Handle material-related errors with type dispatch.
   *
   * @param {string} type - Material error type ('validation' | 'state' | 'relationship')
   * @param {Object} details - Error details
   * @returns {Object} Recovery result
   */
  function handleMaterialError(type, details) {
    if (!bootstrapComplete) return { recovered: false, action: 'none' };

    try {
      const handlers = {
        validation: handleMaterialValidationError,
        state: handleMaterialStateError,
        relationship: handleRelationshipError,
      };

      const handler = handlers[type] || handleGenericMaterialError;
      return handler(details);
    } catch (error) {
      isDev() &&
        console.error('[ErrorHandler] handleMaterialError failed:', error);
      return { recovered: false, action: 'none' };
    }
  }

  function handleMaterialValidationError(details) {
    const { categoryId, error, message } = details || {};

    logError('Material Validation Error', {
      categoryId,
      error,
      message,
      timestamp: Date.now(),
    });

    // PATTERN 10: Use event-driven displayError instead of direct uiUpdater call
    displayError(
      ERROR_TYPES.VALIDATION,
      message || 'Material validation error'
    );
    return attemptMaterialErrorRecovery(categoryId, error);
  }

  function handleMaterialStateError(details) {
    const { categoryId, state, error } = details || {};

    logError('Material State Error', {
      categoryId,
      state,
      error,
      timestamp: Date.now(),
    });

    // PATTERN 9: Request state reset via event (not direct stateManager access)
    publishEvent(EVENTS.STATE_MATERIAL_STATE_RESET_REQUESTED, {
      categoryId,
      source: 'error_handler',
      timestamp: Date.now(),
    });

    return { recovered: true, action: 'state_reset' };
  }

  function handleRelationshipError(details) {
    const { woodId, finishId, error } = details || {};

    logError('Relationship Error', {
      woodId,
      finishId,
      error,
      timestamp: Date.now(),
    });

    return { recovered: true, action: 'clear_incompatible' };
  }

  function handleGenericMaterialError(details) {
    logError('Generic Material Error', { details, timestamp: Date.now() });
    return { recovered: false, action: 'none' };
  }

  function attemptMaterialErrorRecovery(categoryId, error) {
    // PATTERN 9: Return recovery action descriptors without direct stateManager access.
    // The caller (conductor) will apply the recovery action via its own delegation.
    switch (error) {
      case 'invalid_combination':
        return {
          recovered: true,
          action: 'clear_finish',
          categoryId,
        };

      case 'missing_wood':
        return { recovered: true, action: 'clear_all', categoryId };

      case 'invalid_variant':
        return { recovered: true, action: 'revert_to_materials', categoryId };

      default:
        return { recovered: false, action: 'none' };
    }
  }

  /**
   * Log an error for diagnostics (console-only to prevent recursion).
   *
   * @param {string} message - Error message
   * @param {Object} errorContext - Error context and details
   */
  function logError(message, errorContext = {}) {
    try {
      // Add material-specific categorization
      if (
        errorContext.categoryId &&
        (errorContext.woodId || errorContext.finishId)
      ) {
        errorContext.type = 'material_error';
      }

      isDev() && console.error(`[ErrorHandler] ${message}`, errorContext);
    } catch (error) {
      // Absolutely no-throw for the logger
    }
  }

  /**
   * Set up click/keyboard interactions for error message dismissal.
   */
  function setupErrorInteractions() {
    if (!DOM) return;

    try {
      DOM.addEventListener(document, 'click', (event) => {
        const errorMessage = DOM.findClosestBySelector?.(
          event.target,
          '.murphy-door-configurator__error-message'
        );
        if (errorMessage) {
          DOM.removeErrorMessage?.(errorMessage);
        }
      });

      DOM.addEventListener(document, 'keydown', (event) => {
        if (event.key === 'Escape') {
          DOM.clearAllErrorMessages?.();
        }
      });
    } catch (error) {
      isDev() &&
        console.error('[ErrorHandler] setupErrorInteractions failed:', error);
    }
  }

  // ============================================================================
  // PATTERN 4: EVENT CONSUMER HANDLERS (10 points)
  // PATTERN 5: EVENT REPLAY READY (10 points) - bootstrapComplete guard
  // PATTERN 6: BOOTSTRAP SUBSCRIPTION AT MODULE LOAD TIME (10 points)
  // ============================================================================
  if (CoreEventBus && EVENTS) {
    // Bootstrap barrier - set flag when PPD is ready
    const unsubBootstrap = CoreEventBus.subscribe(
      EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        bootstrapComplete = true;

        if (!isInitialized) {
          isInitialized = true;
          setupErrorInteractions();

          publishEvent(EVENTS.ERROR_HANDLER_INITIALIZED, {
            module: 'supportErrorHandler',
            timestamp: Date.now(),
          });

          isDev() &&
            console.log('[ErrorHandler] Initialized after bootstrap complete');
        }
      }
    );
    cleanupHandlers.push(unsubBootstrap);

    // Subscribe to unapply failure events for centralized error handling
    const unsubUnapplyFailed = CoreEventBus.subscribe(
      EVENTS.SELECTION_USER_UNAPPLY_FAILED,
      (event) => {
        if (!bootstrapComplete) return;

        const categoryId = event?.categoryId || 'unknown';
        const errorMessage = event?.error || 'Selection removal failed';

        isDev() &&
          console.error(
            `[ErrorHandler] Unapply failed for category "${categoryId}":`,
            errorMessage,
            event
          );

        displayError(
          ERROR_TYPES.SYSTEM,
          `Failed to remove selection: ${errorMessage}`
        );
      }
    );
    cleanupHandlers.push(unsubUnapplyFailed);

    // Subscribe to system shutdown for cleanup
    const unsubShutdown = CoreEventBus.subscribe(EVENTS.SYSTEM_SHUTDOWN, () => {
      isDev() && console.log('[ErrorHandler] Cleaning up resources');
      cleanupHandlers.forEach((unsub) => {
        try {
          unsub();
        } catch (e) {
          /* no-throw */
        }
      });
      cleanupHandlers.length = 0;
      isInitialized = false;
      bootstrapComplete = false;
    });
    cleanupHandlers.push(unsubShutdown);

    isDev() && console.log('[ErrorHandler] Event subscriptions registered');
  }

  // ============================================================================
  // PATTERN 20: CONDUCTOR SINGLE PUBLICATION (10 points)
  // Public API - registered once on the global namespace
  // ============================================================================
  const errorHandlerAPI = {
    // Frozen error type constants
    ERROR_TYPES,

    // Core operations
    displayError,
    handleError,
    handleCriticalError,
    handleRecoverableError,
    displayUserError,
    clearErrors,

    // Specialized handlers
    handleImageLayerError,
    handleMaterialError,

    // Logging
    logError,

    // Interaction setup (called by conductor if needed)
    setupErrorInteractions,

    // Cleanup for testing
    destroy() {
      cleanupHandlers.forEach((unsub) => {
        try {
          unsub();
        } catch (e) {
          /* no-throw */
        }
      });
      cleanupHandlers.length = 0;
      isInitialized = false;
      bootstrapComplete = false;
    },
  };

  // Register as supportErrorHandler (required by conductor dependency check)
  window.MurphyDoorConfigurator.supportErrorHandler = errorHandlerAPI;

  // Also register as errorHandler for backward compatibility
  // (conductor and state-manager reference window.MurphyDoorConfigurator.errorHandler)
  window.MurphyDoorConfigurator.errorHandler = errorHandlerAPI;
})(window);
